import { infoApi } from '@shared/info-api-config';
import { documentsAndRequests } from '@shared/documents-and-requests-config';
import _ from '@luna/lodash';
import { appointments } from '@retail/appointments-config';
import { aboutYouConfig } from '@retail/about-you-config';
import { contactDataManagement } from '@shared/contact-data-management-config';
import { thirdPartyProviders } from '@shared/third-party-providers-config';
import { messenger } from '@shared/messenger-config';
import { cards } from '@retail/cards-config';
import { overview } from '@retail/overview-config';
import { plugins } from '@retail/plugins-config';
import {
    AccountsCurrentFeatureConfig,
    AccountsFeatureConfig,
    BuildingSocietyFeatureConfig,
    ProductsFeatureConfig,
    SavingsFeatureConfig,
    StatementFeatureConfig,
} from '@retail/products-config';
import { exportFeatures, transactions } from '@retail/transactions-config';
import { navigation } from '@retail/navigation-config';
import { featureConfigMap as store } from '@shared/store-config';
import { supportChatFeatureConfigMap } from '@shared/support-chat-config';
import { securitySettings } from '@shared/security-settings-config';
import { moneyAutomationFeatureConfigMap } from '@retail/money-automation-config';

import { arrayOverwrite, baseFeatureConfig, extendFeatureConfigFromUrl } from './base';
import type { PartialRetailFeatureConfig } from './featureConfig';

export const countryFeatureConfig: PartialRetailFeatureConfig = {
    appointments: appointments.SK,
    aboutYou: aboutYouConfig.SK,
    contactDataManagement: contactDataManagement.SK,
    documentsAndRequests: documentsAndRequests.SK,
    messenger: messenger.SK,
    thirdPartyProviders: thirdPartyProviders.SK,
    cards: cards.SK,
    overview: overview.SK,
    plugins: plugins.SK,
    statements: StatementFeatureConfig.SK,
    transactions: transactions.SK,
    export: exportFeatures.SK,
    savings: SavingsFeatureConfig.SK,
    store: store.SK,
    supportChat: supportChatFeatureConfigMap.SK,
    securitySettings: securitySettings.SK,
    base: {
        country: 'SK',
        newAccountModel: true,
        ibanCountryCode: 'SK',
        intrabankIbanMatch: /(\s*)(SK)(\d\d)(\s*)(0900)(.*?)/.toString(),
        defaultLanguage: 'sk',
        allowedLanguages: ['sk', 'en'],
        availableLanguages: ['sk', 'en', 'en-SZ'],
        preferWebApiLanguage: true,
        sendRequestId: false,
        thousandSeparator: ' ',
        sendLanguageHeader: true,
        useVersionHeaders: false,
        latestVersionHeader: null,
        mainProductAmount: 'disposable',
        loanProductAmount: 'balance',
        enableTraceWebapi: false,
        preferProductI18NNamesOnAliases: true,
        defaultTimezone: 'Europe/Bratislava',
        languageOverrides: {
            en: 'en-SZ',
        },
        passCredentialsInRequest: true,
    },
    footer: {
        links: {
            contacts: {
                sk: 'https://www.slsp.sk/mojgeorge',
                en: 'https://www.slsp.sk/mojgeorge',
            },
            fx: {
                sk: 'https://www.slsp.sk/sk/kurzovy-listok',
                en: 'https://www.slsp.sk/sk/kurzovy-listok',
            },
            security: {
                sk: 'https://www.slsp.sk/sk/ludia/non-product/bezpecnostne-informacie-co-robime-pre-vasu-bezpecnost',
                en: 'https://www.slsp.sk/sk/ludia/non-product/bezpecnostne-informacie-co-robime-pre-vasu-bezpecnost',
            },
            termsAndConditions: {
                sk: 'https://www.slsp.sk/sk/information-book/spotrebitel',
                en: 'https://www.slsp.sk/sk/information-book/spotrebitel',
            },
        },
    },
    navigation: navigation.SK,
    handover: {
        handoverTokenExchange: false,
        sso: true,
    },
    accounts: AccountsFeatureConfig.SK,
    currentAccount: AccountsCurrentFeatureConfig.SK,
    accountReservations: {
        isTransactionTypeUsed: true,
    },
    buildingSociety: BuildingSocietyFeatureConfig.SK,
    pfm: {
        additionalKeywordFields: [
            'merchantName',
            'partnerName',
            'bookingTypeTranslation',
            'location',
            'note',
            'receiverReference',
            'senderReference',
            'paymentReference',
            'additionalTexts.variableSymbol',
            'additionalTexts.specificSymbol',
        ],
    },
    moneyAutomation: moneyAutomationFeatureConfigMap.SK,
    settings: {
        changePassword: false,
        saveLanguageInUppercase: false,
    },
    crm: {
        sevenYearsPluginPromotion: false,
    },
    infoApi: infoApi.SK,
    devTools: {
        availableEnvironments: {
            Local: 'http://localhost:8080',
            Dev: 'https://sk.dev.beeone.at',
            FAT: 'https://george.fat.slsp.sk',
            SIT: 'https://george-sit.slsp.sk',
            UAT: 'https://george.uat.slsp.sk',
        },
    },
    tracking: {
        appId: 'INT83',
        appName: 'GeorgeWeb_Retail_SK',
        instituteId: 772,
    },
    products: ProductsFeatureConfig.SK,
    allowedRedirectDomains: ['slsp.sk'],
};

const featureConfigWithBase = _.merge({}, baseFeatureConfig, countryFeatureConfig, arrayOverwrite);
export const featureConfig = extendFeatureConfigFromUrl(featureConfigWithBase);
